import { EnvironmentBanner, Iconify } from '@btsis/ui';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { errorKey, errorParams } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';

export function SignInPage() {
  const { t } = useTranslation();
  const { login, verifyTwoFactor } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      if (challengeToken) {
        await verifyTwoFactor({ challengeToken, code });
      } else {
        const result = await login({ email, password });
        if (result.status === 'two_factor_required') setChallengeToken(result.challengeToken);
      }
    } catch (caught) {
      setError(caught);
    } finally {
      setPending(false);
    }
  }

  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', p: 2 }}>
      <EnvironmentBanner floating />
      <Card sx={{ p: 3, width: '100%', maxWidth: 420 }}>
        <Stack component="form" spacing={2.5} onSubmit={submit}>
          <Stack spacing={1} alignItems="center" textAlign="center">
            <Iconify icon="solar:box-bold-duotone" width={48} sx={{ color: 'primary.main' }} />
            <Typography variant="h5">
              {challengeToken ? t('field.signIn.twoFactorTitle') : t('field.signIn.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {challengeToken ? t('field.signIn.twoFactorHint') : t('field.signIn.subtitle')}
            </Typography>
          </Stack>

          {error ? <Alert severity="error">{t(errorKey(error), errorParams(error))}</Alert> : null}

          {challengeToken ? (
            <TextField
              label={t('field.signIn.code')}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
            />
          ) : (
            <>
              <TextField
                label={t('field.signIn.email')}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                inputMode="email"
                autoFocus
              />
              <TextField
                label={t('field.signIn.password')}
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
              />
            </>
          )}

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={pending}
            sx={{ height: 52 }}
          >
            {challengeToken ? t('field.signIn.verify') : t('field.signIn.submit')}
          </Button>
        </Stack>
      </Card>
    </Box>
  );
}
